import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import type { RootStore } from 'widget/stores/RootStore';
import {
  WidgetConsentSubmitStatus,
  WidgetConsentsType
} from 'widget/entities/config';
import type {
  IWidgetSubmitStatus,
  WidgetConsents
} from 'widget/entities/config';
import LoadingStageModel from 'widget/models/LoadingStageModel';
import { DEFAULT_CONSENT_CHECKBOX_VALUES } from 'widget/consts/config';
import ConsentFieldModel from 'widget/models/ConsentFieldModel';

type PrivateFields = '_personalDataConsent' | '_mailingConsent';

export class ConsentsStore {
  private _rootStore: RootStore;

  private _personalDataConsent: ConsentFieldModel | null = null;
  private _mailingConsent: ConsentFieldModel | null = null;

  readonly submitStage = new LoadingStageModel();

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;

    makeObservable<ConsentsStore, PrivateFields>(this, {
      _personalDataConsent: observable.ref,
      _mailingConsent: observable.ref,

      personalDataConsent: computed,
      mailingConsent: computed,
      consentsFields: computed,
      isDone: computed,

      init: action,
      submitConsents: action
    });
  }

  get personalDataConsent(): ConsentFieldModel | null {
    return this._personalDataConsent;
  }

  get mailingConsent(): ConsentFieldModel | null {
    return this._mailingConsent;
  }

  getConsent(type: WidgetConsentsType): ConsentFieldModel | null {
    switch (type) {
      case WidgetConsentsType.personalData:
        return this._personalDataConsent;
      case WidgetConsentsType.mailing:
        return this._mailingConsent;
      default:
        return null;
    }
  }

  get consentsFields(): ConsentFieldModel[] {
    const result: ConsentFieldModel[] = [];
    if (this._personalDataConsent) {
      result.push(this._personalDataConsent);
    }
    if (this._mailingConsent) {
      result.push(this._mailingConsent);
    }
    return result;
  }

  private isConsentDone(type: WidgetConsentsType): boolean {
    const consent = this.getConsent(type);
    if (!consent) {
      return true;
    }

    if (consent.isRequired) {
      return consent.status.value === WidgetConsentSubmitStatus.accepted;
    }
    return consent.status.value !== WidgetConsentSubmitStatus.pending;
  }

  get isDone(): boolean {
    return (
      this.isConsentDone(WidgetConsentsType.personalData) &&
      this.isConsentDone(WidgetConsentsType.mailing)
    );
  }

  init(consents: WidgetConsents, statuses: IWidgetSubmitStatus | null): void {
    this._personalDataConsent = consents.personalData.isEnabled
      ? new ConsentFieldModel({
          required: consents.personalData.required,
          enabled:
            DEFAULT_CONSENT_CHECKBOX_VALUES[WidgetConsentsType.personalData],
          status: statuses?.personalDataConsent
        })
      : null;

    this._mailingConsent = consents.mailing.isEnabled
      ? new ConsentFieldModel({
          required: consents.mailing.required,
          enabled: DEFAULT_CONSENT_CHECKBOX_VALUES[WidgetConsentsType.mailing],
          status: statuses?.mailingConsent
        })
      : null;
  }

  submitConsents = async (): Promise<{ isError: boolean }> => {
    if (!this._personalDataConsent && !this._mailingConsent) {
      return { isError: false };
    }

    if (this.submitStage.isLoading) {
      return { isError: true };
    }

    this.submitStage.loading();

    try {
      const response = await this._rootStore.apiStore.submitConsents({
        personal_data: this._personalDataConsent?.enabled.value,
        mailing: this._mailingConsent?.enabled.value
      });

      runInAction(() => {
        if (response.isError) {
          this.submitStage.error();
          return;
        }

        if (this._personalDataConsent) {
          this._personalDataConsent.status.changeValue(
            this._personalDataConsent.enabled.value === true
              ? WidgetConsentSubmitStatus.accepted
              : WidgetConsentSubmitStatus.rejected
          );
        }
        if (this._mailingConsent) {
          this._mailingConsent.status.changeValue(
            this._mailingConsent.enabled.value === true
              ? WidgetConsentSubmitStatus.accepted
              : WidgetConsentSubmitStatus.rejected
          );
        }

        this.submitStage.success();
      });

      return { isError: response.isError };
    } catch {
      runInAction(() => {
        this.submitStage.error();
      });

      return { isError: true };
    }
  };
}
